'use client';

import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { cn } from '../../utils';

const SIDEBAR_WIDTH = '220px';

const SidebarContext = React.createContext<{ open: boolean; setOpen: (open: boolean) => void } | null>(null);

function useSidebar() {
    const context = React.useContext(SidebarContext);
    if (!context) {
        return { open: true, setOpen: () => {}, state: 'expanded' as const };
    }
    return { ...context, state: context.open ? ('expanded' as const) : ('collapsed' as const) };
}

interface SidebarProviderProps extends React.ComponentProps<'div'> {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

function SidebarProvider({ defaultOpen = true, open: controlledOpen, onOpenChange, children, ...props }: SidebarProviderProps) {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;
    const setOpen = React.useCallback(
        (value: boolean) => {
            if (!isControlled) setUncontrolledOpen(value);
            onOpenChange?.(value);
        },
        [isControlled, onOpenChange],
    );
    return (
        <SidebarContext.Provider value={{ open, setOpen }}>
            <div data-slot="sidebar-provider" className="group flex w-full" {...props}>
                {children}
            </div>
        </SidebarContext.Provider>
    );
}

interface SidebarProps extends React.ComponentProps<'div'> {
    side?: 'left' | 'right';
    variant?: 'sidebar' | 'floating' | 'inset';
    collapsible?: 'offcanvas' | 'icon' | 'none';
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
    ({ side = 'left', variant = 'sidebar', collapsible = 'none', className, children, ...props }, ref) => {
        return (
            <div
                ref={ref}
                data-slot="sidebar"
                data-side={side}
                data-variant={variant}
                data-collapsible={collapsible}
                className={cn('flex h-full shrink-0 flex-col bg-sidebar text-sidebar-foreground', 'w-[var(--sidebar-width,14rem)]', className)}
                style={{ '--sidebar-width': SIDEBAR_WIDTH } as React.CSSProperties}
                {...props}
            >
                {children}
            </div>
        );
    },
);
Sidebar.displayName = 'Sidebar';

const SidebarContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, children, ...props }, ref) => (
    <div ref={ref} data-slot="sidebar-content" className={cn('flex min-h-0 flex-1 flex-col overflow-auto', className)} {...props}>
        {children}
    </div>
));
SidebarContent.displayName = 'SidebarContent';

const SidebarGroup = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, children, ...props }, ref) => (
    <div ref={ref} data-slot="sidebar-group" className={cn('relative flex w-full flex-col p-2', className)} {...props}>
        {children}
    </div>
));
SidebarGroup.displayName = 'SidebarGroup';

const SidebarGroupLabel = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'> & { asChild?: boolean }>(
    ({ className, children, asChild, ...props }, ref) => {
        const Comp = asChild ? Slot : 'div';
        return (
            <Comp
                ref={ref}
                className={cn('flex h-8 shrink-0 items-center px-2 text-xs font-semibold text-muted-foreground tracking-wider', className)}
                {...props}
            >
                {children}
            </Comp>
        );
    },
);
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

const SidebarGroupContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(({ className, ...props }, ref) => (
    <div ref={ref} data-slot="sidebar-group-content" className={cn('flex flex-col gap-1', className)} {...props} />
));
SidebarGroupContent.displayName = 'SidebarGroupContent';

const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(({ className, ...props }, ref) => (
    <ul ref={ref} data-slot="sidebar-menu" role="list" className={cn('flex w-full flex-col gap-1', className)} {...props} />
));
SidebarMenu.displayName = 'SidebarMenu';

const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
    <li ref={ref} data-slot="sidebar-menu-item" role="none" className={cn('group/menu-item list-none', className)} {...props} />
));
SidebarMenuItem.displayName = 'SidebarMenuItem';

interface SidebarMenuButtonProps extends React.ComponentProps<'button'> {
    isActive?: boolean;
}

const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(({ className, isActive, children, ...props }, ref) => (
    <button
        ref={ref}
        type="button"
        data-slot="sidebar-menu-button"
        data-active={isActive}
        className={cn(
            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm outline-none transition-colors',
            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            'focus-visible:ring-2 focus-visible:ring-ring',
            isActive && 'bg-sidebar-accent text-sidebar-accent-foreground font-medium',
            '[&_svg]:size-4 [&_svg]:shrink-0',
            className,
        )}
        {...props}
    >
        {children}
    </button>
));
SidebarMenuButton.displayName = 'SidebarMenuButton';

export {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
    useSidebar,
};
